<script setup>
/**
 * custom (2026-08-29 · Fase 1 mídia): a bolha do anexo que ainda não desceu.
 *
 * Por que existe: desde a Fase 1 a mensagem grava ANTES da mídia — o webhook não faz mais
 * I/O externo, e um job busca o binário depois. Isso abre uma janela de alguns segundos em
 * que o anexo existe sem arquivo, e uma janela permanente quando o download desiste.
 *
 * Sem esta bolha, os dois casos cairiam nos players normais com `dataUrl` vazio: player
 * travado em 00:00 e vídeo preto — exatamente o sintoma que a Fase 1 existe para eliminar.
 * O objetivo aqui é que a tela NUNCA minta sobre o que aconteceu.
 *
 * Os dois estados são deliberadamente diferentes em peso: "recebendo" é rotina e some
 * sozinho, então é neutro e discreto; "não baixou" é perda e precisa de cor, porque é o
 * único aviso que o operador vai ter.
 */
import { computed } from 'vue';
import BaseBubble from '../Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useMessageContext } from '../../provider.js';

const { attachments, content } = useMessageContext();

const attachment = computed(() => attachments.value?.[0] ?? {});

const falhou = computed(() => attachment.value.mediaStatus === 'falhou');

const NOMES = {
  audio: 'áudio',
  video: 'vídeo',
  image: 'imagem',
  file: 'arquivo',
};

const nomeDoTipo = computed(() => NOMES[attachment.value.fileType] ?? 'mídia');

const ICONES = {
  audio: 'i-ph-microphone',
  video: 'i-ph-video-camera',
  image: 'i-ph-image',
  file: 'i-ph-file',
};

// ── Idade (Fase 1.1) ─────────────────────────────────────────────────────────────────────
// "Recebendo…" com 5 segundos e com 5 horas eram idênticos na tela. Foi assim que um áudio
// preso passou por normal a noite inteira: o operador não tinha como saber que aquilo já
// tinha parado de ser espera e virado defeito.
const PRESO_MIN = 5;

const minutosParado = computed(() => {
  const desde = attachment.value.mediaSince;
  if (!desde) return null;
  const m = Math.floor((Date.now() - new Date(desde).getTime()) / 60000);
  return Number.isFinite(m) && m >= 0 ? m : null;
});

const preso = computed(
  () =>
    !falhou.value &&
    minutosParado.value !== null &&
    minutosParado.value >= PRESO_MIN
);

const idade = computed(() => {
  const m = minutosParado.value;
  if (m === null || m < 1) return '';
  if (m < 60) return ` (há ${m} min)`;
  const h = Math.floor(m / 60);
  return h === 1 ? ' (há 1 h)' : ` (há ${h} h)`;
});

const icone = computed(() => {
  if (falhou.value) return 'i-ph-warning-circle';
  if (preso.value) return 'i-ph-clock-countdown';
  return ICONES[attachment.value.fileType] ?? 'i-ph-paperclip';
});

const titulo = computed(() => {
  if (falhou.value) return `O ${nomeDoTipo.value} não pôde ser baixado`;
  if (preso.value) return `O ${nomeDoTipo.value} não chegou${idade.value}`;
  return `Recebendo ${nomeDoTipo.value}…${idade.value}`;
});

// O motivo vem do servidor e é texto curto e técnico ("Meta recusou 401"). Aparece porque a
// alternativa — "falhou" sem mais nada — foi exatamente o que fez cada diagnóstico custar
// uma sessão inteira.
//
// Quando não há motivo, o lugar do texto não fica vazio: sem uma saída, o aviso vira só
// má notícia. Em computed e não no template porque o lint proíbe literal na marcação
// (`vue/no-bare-strings-in-template`) — e o CRM é pt-BR travado, então não vai para i18n.
const SAIDA_SEM_MOTIVO =
  'Peça ao cliente para reenviar, ou tente de novo pelo painel de Saúde do WhatsApp.';

// O motivo aparece TAMBÉM enquanto ainda está tentando (Fase 1.1). Antes ele só saía na
// falha definitiva — e um anexo falhando em loop mostrava um "recebendo…" limpo, sem uma
// palavra sobre o erro que já estava gravado no banco.
const motivo = computed(() => {
  if (attachment.value.mediaError) return attachment.value.mediaError;
  return falhou.value || preso.value ? SAIDA_SEM_MOTIVO : '';
});

const tentativas = computed(() => {
  const n = attachment.value.mediaAttempts;
  return n > 0 ? `${n} tentativa${n > 1 ? 's' : ''}` : '';
});
</script>

<template>
  <BaseBubble class="p-3" data-bubble-name="midia-whatsapp">
    <p v-if="content" class="mb-2 whitespace-pre-wrap">{{ content }}</p>

    <div
      class="flex items-start gap-2 rounded-lg px-3 py-2"
      :class="[
        falhou ? 'bg-mj-perdida-bg text-mj-perdida-ink' : '',
        preso ? 'bg-mj-atencao-bg text-mj-atencao-ink' : '',
        !falhou && !preso ? 'bg-n-alpha-2' : '',
      ]"
    >
      <Icon
        :icon="icone"
        class="size-4 shrink-0 mt-px"
        :class="{ 'animate-loader-pulse': !falhou && !preso }"
      />
      <div class="min-w-0">
        <p class="text-mj-13">{{ titulo }}</p>
        <p v-if="motivo" class="text-mj-11 opacity-80 break-words">
          {{ motivo }}
        </p>
        <p v-if="tentativas" class="text-mj-11 opacity-60">{{ tentativas }}</p>
      </div>
    </div>
  </BaseBubble>
</template>
